import * as moment from 'moment';
import React, { useState } from 'react';
import { DateRangePicker } from 'react-dates';
import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from 'reactstrap';
import MultiSelectSearch from '../../../common/MultiSelectSearch';
import Button from 'reactstrap/lib/Button';
import '../../../../styles/AdminDashboardPage.css';

enum FilterType {
    Property,
    Company,
    City,
    State,
}

interface dropInterface {
    id: number;
    name: string;
    toolTip?: string;
}


interface AnalyticsFilterProps {
    selectedOption: string;
    setSelectedOption: any;
    startDate: moment.Moment | null;
    endDate: moment.Moment | null;
    setStartDate: any;
    setEndDate: any;
    dateType: {
        lastMonth: string;
        monthToDate: string;
        lastSixMonths: string;
        lastThreeMonths: string;
        yearToDate: string;
    };
    dPropertyList: dropInterface[];
    dCompaniesList: dropInterface[];
    dCityList: dropInterface[];
    dStatesList: dropInterface[];
    selectedPropertyIds: number[];
    selectedCompanyIds: number[];
    selectedCityIds: number[];
    selectedStateIds: number[];
    setSelectedPropertyIds: (ids: number[]) => void;
    setSelectedCompanyIds: (ids: number[]) => void;
    setSelectedCityIds: (ids: number[]) => void;
    setSelectedStateIds: (ids: number[]) => void;
    handleSelectedPropertyOption: (option: any, type: number, selectedIds: number[], setSelectedIds: any) => void;
    setAlertMessage: any;
    setViewDialog: any;
    getDashboardData: any;
    resetData: any;
    showOnDevice: boolean;
    addAll: any;
    clearAll: any;

}

const AnalyticsFilter: React.FC<AnalyticsFilterProps> = ({
    selectedOption,
    setSelectedOption,
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    dateType,
    dPropertyList,
    dCompaniesList,
    dCityList,
    dStatesList,
    selectedPropertyIds,
    selectedCompanyIds,
    selectedCityIds,
    selectedStateIds,
    setSelectedPropertyIds,
    setSelectedCompanyIds,
    setSelectedCityIds,
    setSelectedStateIds,
    handleSelectedPropertyOption,
    setAlertMessage,
    setViewDialog,
    getDashboardData,
    resetData,
    showOnDevice,
    clearAll,
    addAll
}) => {

    const [datePickerFocus, setDatePickerFocus] = useState<'startDate' | 'endDate' | null>(null);
    const [dropdownOpen, setDropdownOpen] = useState(false);

    const toggle = () => setDropdownOpen(prevState => !prevState);

    const handleSelectOption = (option: string) => {
        setSelectedOption(option);
        if (option === dateType.lastSixMonths) {
            const sixMonthsAgo = moment().subtract(6, 'months');
            setStartDate(sixMonthsAgo);
            setEndDate(moment());
        } else if (option === dateType.lastThreeMonths) {
            const threeMonthsAgo = moment().subtract(3, 'months');
            setStartDate(threeMonthsAgo);
            setEndDate(moment());
        } else if (option === dateType.yearToDate) {
            const startOfYear = moment().startOf('year');
            setStartDate(startOfYear);
            setEndDate(moment());
        } else if (option === dateType.monthToDate) {
            const startOfMonth = moment().startOf('months');
            setStartDate(startOfMonth);
            setEndDate(moment());
        } else if (option === dateType.lastMonth) {
            const startOfMonth = moment().subtract(1,'months');
            setStartDate(startOfMonth);
            setEndDate(moment());
        }
    };


    return (
        <div style={{ display: showOnDevice? 'flex': 'none', flexDirection: 'column', gap: '20px', }}>
            <Dropdown isOpen={dropdownOpen} toggle={toggle}>
            <DropdownToggle caret style={{ backgroundColor: 'white', color: 'black', border: '1px solid #ced4da', width: '100%', display: 'flex', alignItems: 'center' }}>
                <span style={{ flexGrow: 1, textAlign: 'start' }}>{selectedOption || 'Pick Date Range'}</span>
            </DropdownToggle>
            <DropdownMenu style={{ width: '100%' }}>
                <DropdownItem onClick={() => handleSelectOption(dateType.lastMonth)} active={selectedOption === dateType.lastMonth}>Last 30 Days</DropdownItem>
                <DropdownItem onClick={() => handleSelectOption(dateType.lastThreeMonths)} active={selectedOption === dateType.lastThreeMonths}>Last 3 Months</DropdownItem>
                <DropdownItem onClick={() => handleSelectOption(dateType.lastSixMonths)} active={selectedOption === dateType.lastSixMonths}>Last 6 Months</DropdownItem>
                <DropdownItem onClick={() => handleSelectOption(dateType.monthToDate)} active={selectedOption === dateType.monthToDate}>Month To Date</DropdownItem>
                <DropdownItem onClick={() => handleSelectOption(dateType.yearToDate)} active={selectedOption === dateType.yearToDate}>Year To Date</DropdownItem>
            </DropdownMenu>
        </Dropdown>
        <DateRangePicker
                small={true}
                showClearDates= {false}
                startDate={startDate ? moment(startDate) : null}
                endDate={endDate ? moment(endDate) : null}
                onDatesChange={(arg: {
                    startDate: moment.Moment | null;
                    endDate: moment.Moment | null;
                }) => {
                    if (arg.startDate) {
                        setStartDate(arg.startDate);
                    }
                    if (arg.endDate) {
                        setEndDate(arg.endDate);
                    }
                }}
                focusedInput={datePickerFocus}
                onFocusChange={focusedOptions => setDatePickerFocus(focusedOptions)}
                startDateId="prospects-start-date"
                endDateId="prospects-end-date"
                isOutsideRange={(day) => moment().isBefore(day, 'day')}
                hideKeyboardShortcutsPanel={true}
        />
        <MultiSelectSearch
            heading = {"Company"}
            listItems = {dCompaniesList}
            type={FilterType.Company}
            selectedIds = {selectedCompanyIds}
            setSelectedIds = {setSelectedCompanyIds}
            handleSelectedPropertyOption={handleSelectedPropertyOption}
            clearAll={clearAll}
            addAll={addAll}
        />
        <MultiSelectSearch
            heading = {"Property"}
            type={FilterType.Property}
            listItems = {dPropertyList}
            selectedIds = {selectedPropertyIds}
            setSelectedIds = {setSelectedPropertyIds}
            handleSelectedPropertyOption={handleSelectedPropertyOption}
            clearAll={clearAll}
            addAll={addAll}
        />
        <MultiSelectSearch
            heading = {"City"}
            listItems = {dCityList}
            type={FilterType.City}
            selectedIds = {selectedCityIds}
            setSelectedIds = {setSelectedCityIds}
            handleSelectedPropertyOption={handleSelectedPropertyOption}
            clearAll={clearAll}
            addAll={addAll}
        />
        <MultiSelectSearch
            heading = {"State"}
            listItems = {dStatesList}
            type={FilterType.State}
            selectedIds = {selectedStateIds}
            setSelectedIds = {setSelectedStateIds}
            handleSelectedPropertyOption={handleSelectedPropertyOption}
            clearAll={clearAll}
            addAll={addAll}
        />
        <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', gap: '20px', marginBottom: '12px' }}>
        <Button
            text={""}
            type={'submit'}
            className={"btn-primary"}
            onClick={()=>{
                if(selectedPropertyIds.length==0){
                    setAlertMessage("Atlease one property should be selected");
                    setViewDialog(true);
                }else{
                    getDashboardData();
                }
            }}
        >Search</Button>
        <Button
            text={""}
            type={'submit'}
            className={"btn-light"}
            onClick={resetData}
        >Reset</Button>
        </div>
        </div>
    );
};

export default AnalyticsFilter;



// WEBPACK FOOTER //
// ./src/components/pages/admin/analyticsDashboardWidgets/analyticsFilter.tsx